"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AccessRecorder = void 0;
exports._createDeepNoopObject = _createDeepNoopObject;
exports.computeTemplateAccesses = computeTemplateAccesses;
exports.mergeTemplateAccesses = mergeTemplateAccesses;
const debug_1 = __importDefault(require("debug"));
const lodash_1 = __importDefault(require("lodash"));
const viem = __importStar(require("viem"));
const template_1 = require("./utils/template");
const types_1 = require("./types");
const debug = (0, debug_1.default)('cannon:builder:access-recorder');
class ExtendableProxy {
    constructor(defaultValue) {
        this.accessed = new Map();
        return new Proxy(this, {
            get: (obj, prop) => {
                if (prop === 'accessed' || prop === 'getAccesses') {
                    return obj[prop];
                }
                if (!this.accessed.has(prop)) {
                    this.accessed.set(prop, defaultValue === undefined ? new AccessRecorder() : defaultValue);
                }
                if (typeof prop === 'symbol') {
                    return () => '';
                }
                else if (types_1.CannonHelperContext[prop]) {
                    // return a dummy function which returns nothing.
                    // this increases the probability that an expression will work out in the template
                    return lodash_1.default.noop;
                }
                return this.accessed.get(prop);
            },
        });
    }
}
class AccessRecorder extends ExtendableProxy {
    getAccesses(depth, cur = 1) {
        if (cur == depth) {
            return Array.from(this.accessed.keys());
        }
        const acc = [];
        for (const k in this.accessed.keys()) {
            acc.push(k);
            acc.push(...this.accessed
                .get(k)
                .getAccesses(depth, (cur || 1) + 1)
                .map((a) => `${k}.${a}`));
        }
        return acc;
    }
}
exports.AccessRecorder = AccessRecorder;
/**
 * Setup the template context.
 * @param possibleNames - The possible names to setup the context for
 * @returns The template context
 */
function setupTemplateContext(possibleNames = []) {
    // Create a fake helper context, so the render works but no real functions are called
    const fakeHelperContext = _createDeepNoopObject(types_1.CannonHelperContext);
    const recorders = {
        // Include base context variables, no access recording as they are always available
        chainId: 0,
        timestamp: 0,
        package: { version: '0.0.0' },
        ...fakeHelperContext,
        // Add access recorders for the base context variables, these are the ones
        // used to calculate dependencies beween steps
        contracts: new AccessRecorder(),
        imports: new AccessRecorder(),
        extras: new AccessRecorder(),
        txns: new AccessRecorder(),
        // For settings, we give it a zeroAddress as a best case scenarion that is going
        // to be working for most cases.
        // e.g., when calculating a setting value for 'settings.owners.split(',')' or 'settings.someNumber' will work.
        settings: new AccessRecorder(viem.zeroAddress),
    };
    // add possible names
    for (const n of possibleNames) {
        recorders[n] = new AccessRecorder();
    }
    return recorders;
}
function _createDeepNoopObject(obj) {
    if (lodash_1.default.isFunction(obj)) {
        return lodash_1.default.noop;
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => _createDeepNoopObject(item));
    }
    if (lodash_1.default.isPlainObject(obj)) {
        return lodash_1.default.mapValues(obj, (value) => _createDeepNoopObject(value));
    }
    return obj;
}
/**
 * Collect the accesses from the recorders.
 * @param recorders - The recorders to collect accesses from
 * @param possibleNames - The possible names to collect accesses from
 * @returns The accesses
 */
function collectAccesses(recorders, possibleNames) {
    const accesses = [];
    for (const recorder of Object.keys(recorders)) {
        if (recorders[recorder] instanceof AccessRecorder) {
            if (possibleNames.includes(recorder) && recorders[recorder].accessed.size > 0) {
                accesses.push(recorder);
            }
            else {
                accesses.push(...Array.from(recorders[recorder].accessed.keys()).map((a) => `${recorder}.${a}`));
            }
        }
    }
    return accesses;
}
/**
 * Compute the accesses from the template.
 * @param str - The template to compute accesses from
 * @param possibleNames - The possible names to compute accesses from
 * @returns The accesses
 */
function computeTemplateAccesses(str, possibleNames = []) {
    if (!str) {
        return { accesses: [], unableToCompute: false };
    }
    const recorders = setupTemplateContext(possibleNames);
    try {
        // we give it "true" for safeContext to avoid cloning and freezing of the object
        // this is because we want to keep the access recorder, and is not a security risk
        // if the user can modify that object
        (0, template_1.template)(str, recorders, true);
        const accesses = collectAccesses(recorders, possibleNames);
        return { accesses, unableToCompute: false };
    }
    catch (err) {
        debug('Template execution failed:', err);
        return { accesses: [], unableToCompute: true };
    }
}
/**
 * Merge two template access computation results.
 * @param r1 - The first result
 * @param r2 - The second result
 * @returns The merged result
 */
function mergeTemplateAccesses(r1, r2) {
    return {
        accesses: [...r1.accesses, ...r2.accesses],
        unableToCompute: r1.unableToCompute || r2.unableToCompute,
    };
}
//# sourceMappingURL=access-recorder.js.map